import { checkDate } from "capitalrx-utilities";
import { GeneralTypes } from "@/types";
import { basisOfReimbursement } from "@/App/constants/constants";

import {
  TestClaimFormattedAdditionalMessage,
  TestClaimFormattedDURMessage,
  TestClaimFormattedPricingData,
  TestClaimFormattedResponseData,
  TestClaimRawResponseData,
  TestClaimResponseHeaderFields,
  TestClaimResponsePriceField,
  TestClaimResponsePricingCategories,
} from "@/types/appTypes/transmissions";

import { ClaimTypes } from "@/types";

type ClaimFields = GeneralTypes.Dictionary<string | number | boolean | Array<number> | Array<string> | object | Array<object>>;

const amountsPaid: Array<TestClaimResponsePriceField> = [
  {
    label: "Ingredient Cost Paid",
    field: "ingredient_cost_paid",
  },
  {
    label: "Dispensing Fee Paid",
    field: "dispensing_fee_paid",
  },
  {
    label: "Total Amount Paid",
    field: "total_amount_paid",
  },
  {
    label: "Vaccine Admin Fee",
    field: "incentive_amount_paid",
  },
  {
    label: "Compound Level of Effort Fee",
    field: "level_of_effort_fee",
  },
  {
    label: "Final Price Type",
    field: "basis_of_reimbursement_determination",
  },
];

const patientResponsibility: Array<TestClaimResponsePriceField> = [
  {
    label: "Patient Pay Amount",
    field: "patient_pay_amount",
  },
  {
    label: "Amount of Copay",
    field: "amount_of_copay",
  },
  {
    label: "Amount of Coinsurance",
    field: "amount_of_coinsurance",
  },
];

const accumulatorAmounts: Array<TestClaimResponsePriceField> = [
  {
    label: "Accumulated Deductible Amount",
    field: "accumulated_deductible_amount",
  },
  {
    label: "Remaining Deductible Amount",
    field: "remaining_deductible_amount",
  },
  {
    label: "Remaining Benefit Amount",
    field: "remaining_benefit_amount",
  },
  {
    label: "Remaining Copay First Amount",
    field: ["accumulations_applied", "copay_first", "amount_remaining"],
  },
];

const medicarePartDAccumulatorAmounts: Array<TestClaimResponsePriceField> = [
  {
    label: "Deductible Phase Amount",
    field: "deductibleAmount",
  },
  {
    label: "Initial Benefit Phase Amount",
    field: "initialAmount",
  },
  {
    label: "Coverage Gap Phase Amount",
    field: "coverageGapAmount",
  },
  {
    label: "Catastrophic Coverage Phase Amount",
    field: "catastrophicAmount",
  },
];

const amountsApplied: Array<TestClaimResponsePriceField> = [
  {
    label: "Amount Applied to Periodic Deductible",
    field: "amount_applied_to_periodic_deductible",
  },
  {
    label: "Amount Applied to Periodic Maximum Out of Pocket",
    field: "amount_applied_to_periodic_maximum_out_of_pocket",
  },
  {
    label: "Amount Applied to Periodic Maximum Benefit",
    field: "amount_applied_to_periodic_maximum_benefit",
  },
  {
    label: "Amount Exceeding Periodic Benefit Maximum",
    field: "amount_exceeding_periodic_benefit_maximum",
  },
  {
    label: "Amount Applied to Copay First",
    field: ["accumulations_applied", "copay_first", "amount_applied"],
  },
];

const amountsAttributed: Array<TestClaimResponsePriceField> = [
  {
    label: "Amount Attributed To Product Selection Brand Drug",
    field: "amount_attributed_to_product_selection_brand_drug",
  },
  {
    label: "Amount Attr. to Product Selection Non-Preferred",
    field: "amount_attributed_to_product_selection_non_preferred",
  },
  {
    label: "Amount Attr. to Product Selection Brand Non-Preferred",
    field: "amount_attributed_to_product_selection_brand_non_preferred",
  },
  {
    label: "Amount Attributed to Provider Network Selection",
    field: "amount_attributed_to_provider_network_selection",
  },
  {
    label: "Amount Attributed to Percentage Tax",
    field: "amount_attributed_to_percentage_tax",
  },
  {
    label: "Amount Attributed to Processor Fee",
    field: "amount_attributed_to_processor_fee",
  },
  {
    label: "Amount Attributed to Regulatory Fee",
    field: "amount_attributed_to_regulatory_fee",
  },
];

const taxAmounts: Array<TestClaimResponsePriceField> = [
  {
    label: "Percentage Tax Amount Paid",
    field: "percentage_tax_amount_paid",
  },
  {
    label: "Patient Percentage Tax Amount",
    field: "patient_percentage_tax_amount",
  },
  {
    label: "Percentage Tax Rate Paid",
    field: "percentage_tax_rate_paid",
  },
];

const otherAmountsPaid: Array<TestClaimResponsePriceField> = [
  {
    label: "Other Amount Paid #1",
    field: "other_amount_paid_1",
  },
  {
    label: "Other Amount Paid #2",
    field: "other_amount_paid_2",
  },
  {
    label: "Other Amount Paid #3",
    field: "other_amount_paid_3",
  },
  {
    label: "Other Payer Amount Recognized",
    field: "other_payer_amount_recognized",
  },
  {
    label: "Point of Service Rebate Amount",
    field: "point_of_service_rebate_amount",
  },
  {
    label: "Patient Pay Reduction Due To Point of Service Rebate",
    field: "patient_pay_reduction_due_to_point_of_service_rebate",
  },
  {
    label: "Plan Pay Reduction Due To Point of Service Rebate",
    field: "plan_pay_reduction_due_to_point_of_service_rebate",
  },
];

const priceCategories: TestClaimResponsePricingCategories = {
  amountsPaid,
  patientResponsibility,
  accumulatorAmounts,
  medicarePartDAccumulatorAmounts,
  amountsApplied,
  amountsAttributed,
  taxAmounts,
  otherAmountsPaid,
};

const renderedPricingFields = [
  "ingredient_cost_paid",
  "dispensing_fee_paid",
  "total_amount_paid",
  "patient_pay_amount",
  "amount_of_copay",
  "amount_of_coinsurance",
  "accumulated_deductible_amount",
  "remaining_deductible_amount",
  "remaining_benefit_amount",
  "amount_applied_to_periodic_deductible",
  "amount_applied_to_periodic_maximum_benefit",
  "amount_applied_to_periodic_maximum_out_of_pocket",
  "amount_attributed_to_product_selection_brand_drug",
  "amount_attributed_to_percentage_tax",
  "amount_exceeding_periodic_benefit_maximum",
  "amount_attributed_to_product_selection_non_preferred",
  "amount_attributed_to_provider_network_selection",
  "amount_attributed_to_product_selection_brand_non_preferred",
  "amount_attributed_to_coverage_gap",
  "amount_attributed_to_processor_fee",
  "amount_attributed_to_regulatory_fee",
  "percentage_tax_amount_paid",
  "percentage_tax_rate_paid",
  "other_amount_paid_1",
  "other_amount_paid_2",
  "other_amount_paid_3",
  "other_payer_amount_recognized",
  "accumulations_applied",
  "patient_pay_reduction_due_to_point_of_service_rebate",
  "plan_pay_reduction_due_to_point_of_service_rebate",
  "point_of_service_rebate_amount",
  "incentive_amount_paid",
  "level_of_effort_fee",
  "basis_of_reimbursement_determination",
  "deductibleAmount",
  "initialAmount",
  "coverageGapAmount",
  "catastrophicAmount",
];

const renderedHeaderFields = ["transaction_response_status", "service_provider_id", "date_of_service"];

const renderedDURFields = [
  "dur_free_text_message",
  "dur_additional_text",
  "clinical_significance_code",
  "database_indicator",
  "other_pharmacy_indicator",
  "other_prescriber_indicator",
  "previous_date_of_fill",
  "quantity_of_previous_fill",
  "reason_for_service_code",
];

const formatKey = (field: string): string => {
  return field.replace(/_./g, (chunk) => chunk.toUpperCase()[1]);
};

const formatHeaderFields = (headerFields: ClaimFields): TestClaimResponseHeaderFields => {
  return {
    id: headerFields["id"],
    source: headerFields["source"],
    transactionResponseStatus: headerFields["transaction_response_status"],
    dateOfService: checkDate(headerFields["date_of_service"]?.toString()),
    serviceProviderID: headerFields["service_provider_id"],
  };
};

const formatClaimMessages = (additionalMessageFields: ClaimFields): Array<TestClaimFormattedAdditionalMessage> => {
  // Concatenate the character limited lines into one message.

  let fieldIndex = 1;
  let finalMessage = "";
  const formattedMessages: Array<TestClaimFormattedAdditionalMessage> = [];


  Object.entries(additionalMessageFields).forEach((_) => {
    const textFieldKey = `additional_message_information_${fieldIndex}`;
    const continuityFieldKey = `additional_message_information_continuity_${fieldIndex}`;
    fieldIndex++; // safe to advance at this point since keys are already declared.

    // Add current line to full message
    if (additionalMessageFields[textFieldKey]) {
      finalMessage += additionalMessageFields[textFieldKey];
    }

    if (additionalMessageFields[continuityFieldKey]) {
      /* If the continuity character is present, the next line is part of this message.
         Do not clear the message. */
      return;
    }

    if (!additionalMessageFields[continuityFieldKey] && finalMessage !== "") {
      /* The continuity character is not present. This is the last line of this message.
         Add to output and clear the current message. */
      formattedMessages.push({
        message: finalMessage,
      });

      finalMessage = "";
    }
  });

  if (formattedMessages.length < 1) {
    return [];
  }

  return formattedMessages;
};

const formatDURMessages = (durMessageFields: ClaimFields): Array<TestClaimFormattedDURMessage> => {
  /*
  Formats the DUR Message fields into individual message boxes, filling in N/A where necessary.
  Loop maxes at 3 messages according to NCPDP standard.
  */

  const formattedDURMessages: Array<TestClaimFormattedDURMessage> = [];
  for (let i = 1; i < 4; i++) {
    const durMessage: TestClaimFormattedDURMessage = {} as TestClaimFormattedDURMessage;

    /* Transform field names into flattened format.
       Then check for each flattened key in inputted fields,
       adding its value or N/A to final object after converting to camelCase. */
    for (const field of renderedDURFields) {
      const flattened_field = field + `_${i}`;
      durMessage[formatKey(field)] = flattened_field in durMessageFields ? durMessageFields[flattened_field] : "N/A";
    }

    if (durMessage["durFreeTextMessage"] && durMessage["durFreeTextMessage"] !== "N/A") {
      formattedDURMessages.push(durMessage);
    }
  }

  if (formattedDURMessages.length < 1) {
    return [];
  }

  return formattedDURMessages;
};

const checkForNonDollarValues = (pricingFields, field) => {
  if (field === "basis_of_reimbursement_determination") {
    return field in pricingFields && pricingFields[field] !== null ? `${pricingFields[field]}` : "N/A";
  }

  return field in pricingFields && pricingFields[field] !== null ? `$${pricingFields[field]}` : "N/A";
};

const formatPricingData = (pricingFields: ClaimFields): TestClaimFormattedPricingData => {
  // Format pricing data with either dollar signs or N/A for missing data.
  const returnedFields: TestClaimFormattedPricingData = {};

  Object.keys(priceCategories).forEach((category) => {
    returnedFields[category] = [];

    priceCategories[category].forEach(({ field, label }) => {
      let value = "N/A";

      if (typeof field === "string") {
        value = checkForNonDollarValues(pricingFields, field);
      } else if (typeof field === "object") {
        let valueObj: Object = pricingFields;
        for (const subfield of field) {
          if (subfield in valueObj) {
            if (typeof valueObj[subfield] === "object") {
              valueObj = valueObj[subfield];
              continue;
            }
            value = `$${valueObj[subfield]}`;
          } else {
            value = "N/A";
            break;
          }
        }
      }

      returnedFields[category].push({
        label,
        value,
      });
    });
  });

  return returnedFields;
};

const getLevelOfEffortFee = (data) => {
  if (data.other_amount_paid_qualifier_1 === "09") return data.other_amount_paid_1;
  if (data.other_amount_paid_qualifier_2 === "09") return data.other_amount_paid_2;
  if (data.other_amount_paid_qualifier_3 === "09") return data.other_amount_paid_3;
};

export const formatTestClaimResponse = (rawResponseData: TestClaimRawResponseData | ClaimTypes.Claim): TestClaimFormattedResponseData => {
  const additionalMessageFields: ClaimFields = {};
  const rejectCodesFields: ClaimFields = {};
  const pricingFields: ClaimFields = {};
  const durFields: ClaimFields = {};
  const headerFields: ClaimFields = {};

  const levelOfEfforFee = getLevelOfEffortFee(rawResponseData);

  const amounts = {
    deductibleAmount: null,
    initialAmount: null,
    coverageGapAmount: null,
    catastrophicAmount: null,
  };

  const phaseMapping = {
    "01": "deductibleAmount",
    "02": "initialAmount",
    "03": "coverageGapAmount",
    "04": "catastrophicAmount",
  };

  for (let i = 1; i <= 4; i++) {
    if (rawResponseData[`benefit_stage_qualifier_${i}`]) {
      amounts[phaseMapping[rawResponseData[`benefit_stage_qualifier_${i}`] as string]] = rawResponseData[`benefit_stage_amount_${i}`];
    } else {
      break;
    }
  }

  const newrawResponseData = {
    ...rawResponseData,
    ...amounts,
  };

  if (levelOfEfforFee) {
    newrawResponseData["level_of_effort_fee"] = levelOfEfforFee;
  }

  if (rawResponseData.basis_of_reimbursement_determination) {
    newrawResponseData["basis_of_reimbursement_determination"] = basisOfReimbursement[rawResponseData.basis_of_reimbursement_determination];
  }

  Object.keys(newrawResponseData).forEach((field: string) => {
    if (field.match("additional_message_information")) {
      additionalMessageFields[field] = newrawResponseData[field];
    }

    if (field.match("reject_code")) {
      rejectCodesFields[field] = newrawResponseData[field];
    }

    if (renderedPricingFields.includes(field)) {
      pricingFields[field] = newrawResponseData[field];
    }

    if (renderedHeaderFields.includes(field)) {
      headerFields[field] = newrawResponseData[field];
    }

    if (renderedDURFields.find((dur) => field.match(dur))) {
      durFields[field] = newrawResponseData[field];
    }
  });

  const formattedResponseData: TestClaimFormattedResponseData = {
    headerFields: {
      id: "",
      source: "test",
      transactionResponseStatus: "",
      serviceProviderID: "",
      dateOfService: "",
    },
    pricingFields: {},
    message: "",
    additionalMessages: [],
    rejectCodes: [],
    durMessages: [],
    reasonsForRejection: [],
    reasonsForAdjudication: [],
    log_key: null,
  };

  if (Object.keys(durFields).length > 0) {
    formattedResponseData["durMessages"] = formatDURMessages(durFields);
  }

  if (newrawResponseData.response_message) {
    formattedResponseData["message"] = newrawResponseData.response_message;
  }

  if (Object.keys(additionalMessageFields).length > 0) {
    formattedResponseData["additionalMessages"] = formatClaimMessages(additionalMessageFields);
  }

  formattedResponseData["rejectCodes"] = Object.values(rejectCodesFields)
    .filter((code: string) => code !== null)
    .map((code: string) => ({ rejectCode: code }));

  formattedResponseData["pricingFields"] = formatPricingData(pricingFields);
  formattedResponseData["headerFields"] = formatHeaderFields(headerFields);

  if (rawResponseData.reasons_for_rejection) {
    const reasonsForRejectionMessages = [];
    for (const message of (rawResponseData.reasons_for_rejection as Array<string>)) {
      reasonsForRejectionMessages.push({ message: message });
    }
    formattedResponseData.reasonsForRejection = reasonsForRejectionMessages;
  }

  if (rawResponseData.reasons_for_adjudication) {
    const reasonsForAdjudicationMessages = [];
    for (const message of (rawResponseData.reasons_for_adjudication as Array<string>)) {
      reasonsForAdjudicationMessages.push({ message: message });
    }
    formattedResponseData.reasonsForAdjudication = reasonsForAdjudicationMessages;
  }

  if (typeof rawResponseData.log_key === "string") {
    formattedResponseData.log_key = rawResponseData.log_key;
  }

  return formattedResponseData;
};

interface TransmissionCodes {
  professional_service_code_1?: string;
  professional_service_code_2?: string;
  professional_service_code_3?: string;
  professional_service_code_4?: string;
  professional_service_code_5?: string;
  professional_service_code_6?: string;
  professional_service_code_7?: string;
  professional_service_code_8?: string;
  professional_service_code_9?: string;
  dur_pps_level_of_effort_1?: string;
  dur_pps_level_of_effort_2?: string;
  dur_pps_level_of_effort_3?: string;
  dur_pps_level_of_effort_4?: string;
  dur_pps_level_of_effort_5?: string;
  dur_pps_level_of_effort_6?: string;
  dur_pps_level_of_effort_7?: string;
  dur_pps_level_of_effort_8?: string;
  dur_pps_level_of_effort_9?: string;
  reason_for_service_code_1?: string;
  reason_for_service_code_2?: string;
  reason_for_service_code_3?: string;
  reason_for_service_code_4?: string;
  reason_for_service_code_5?: string;
  reason_for_service_code_6?: string;
  reason_for_service_code_7?: string;
  reason_for_service_code_8?: string;
  reason_for_service_code_9?: string;
  result_of_service_code_1?: string;
  result_of_service_code_2?: string;
  result_of_service_code_3?: string;
  result_of_service_code_4?: string;
  result_of_service_code_5?: string;
  result_of_service_code_6?: string;
  result_of_service_code_7?: string;
  result_of_service_code_8?: string;
  result_of_service_code_9?: string;
}

export const transformRepeatedField = (repeatedField: string, column: string): TransmissionCodes => {
  const repeatedFields = {};

  const codes = repeatedField.trim();

  // empty space, move right along
  if (!codes) {
    return {};
  }

  if (repeatedField.includes(",")) {
    const differentCodes = codes.split(",");
    const fixedDifferentCodes = differentCodes.map((code: string, index: number) => code.trim().toUpperCase());
    fixedDifferentCodes.forEach((code: string, index: number) => {
      repeatedFields[`${column}_${index + 1}`] = code.trim().toUpperCase();
    });
  } else {
    repeatedFields[`${column}_1`] = codes.toUpperCase();
  }

  return repeatedFields;
};
